import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import logo from "./assets/snap4me_logo.png";
import loginBackground from "./assets/login-dashboard.png";

const API_URL =
  process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

function ForgotPassword() {
  const navigate = useNavigate();

  // Current screen step: "email" | "otp" | "reset"
  const [step, setStep] = useState("email");

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // =========================================================
  // STEP 1: SEND OTP
  // =========================================================
  const handleSendOTP = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");

    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/api/auth/forgot-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: email.trim() }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            data.message ||
            "Unable to send OTP. Please try again."
        );
      }

      // Move to OTP screen
      setStep("otp");

      setSuccess(
        data.message ||
          "OTP has been sent to your registered email address."
      );
    } catch (err) {
      console.error("SEND OTP ERROR:", err);
      setError(
        err.message ||
          "Unable to send OTP. Please check your network and try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // =========================================================
  // RESEND OTP
  // =========================================================
  const handleResendOTP = async () => {
    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/api/auth/forgot-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: email.trim() }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            data.message ||
            "Unable to resend OTP. Please try again."
        );
      }

      setSuccess(
        data.message ||
          "A new OTP has been sent to your registered email address."
      );
    } catch (err) {
      console.error("RESEND OTP ERROR:", err);
      setError(
        err.message ||
          "Unable to resend OTP. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // =========================================================
  // STEP 2: VERIFY OTP
  // =========================================================
  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");

    if (!otp.trim()) {
      setError("Please enter the OTP.");
      return;
    }

    if (otp.trim().length !== 6) {
      setError("OTP must be 6 digits.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/api/auth/verify-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email.trim(),
            otp: otp.trim(),
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.detail ||
            data.message ||
            "Invalid or expired OTP."
        );
      }

      // Backend returns reset token
      if (!data.reset_token) {
        throw new Error(
          "Reset token was not returned by the server."
        );
      }

      // Save reset token for next screen
      setResetToken(data.reset_token);

      // Move to RESET PASSWORD screen
      setStep("reset");

      setSuccess(
        "OTP verified successfully. Please create your new password."
      );
    } catch (err) {
      console.error("VERIFY OTP ERROR:", err);

      setError(
        err.message ||
          "Unable to verify OTP. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // =========================================================
  // STEP 3: RESET PASSWORD
  // =========================================================
  const handleResetPassword = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");

    if (!newPassword) {
      setError("Please enter your new password.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    if (!confirmPassword) {
      setError("Please confirm your new password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!resetToken) {
      setError("Invalid reset session. Please request a new OTP.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/api/auth/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email.trim(),
            reset_token: resetToken,
            new_password: newPassword,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.detail ||
            data.message ||
            "Unable to reset password."
        );
      }

      setSuccess(
        "Password reset successfully. Redirecting to login..."
      );

      // Clear sensitive state
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
      setResetToken("");

      // Go to login screen
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (err) {
      console.error("RESET PASSWORD ERROR:", err);

      setError(
        err.message ||
          "Unable to reset password. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="dashboard-login-root">
      {/* LEFT PANEL */}
      <div
        className="dashboard-login-hero"
        style={{
          backgroundImage: `
            linear-gradient(to bottom right, rgba(47, 49, 52, 1), rgba(55, 114, 255, 0.46)), 
            url(${loginBackground})
          `,
        }}
      >
        <div className="hero-content">
          <div className="brand-header">
            <img
              src={logo}
              alt="Snap4Me Logo"
              className="brand-logo-img"
            />
          </div>

          <div className="hero-body">
            <span className="hero-eyebrow">
              ACCOUNT RECOVERY
            </span>

            <h2>
              Reset Your Access
              <br />
              <span>Secure Authentication</span>
            </h2>

            <p>
              Receive a one-time verification code on your registered email address to securely reset your account credentials.
            </p>

            <div className="hero-feature-list">
              <div className="hero-feature">
                <span className="feature-icon">✓</span>
                <span>Time-Sensitive OTP Verification</span>
              </div>

              <div className="hero-feature">
                <span className="feature-icon">✓</span>
                <span>End-to-End Credential Encryption</span>
              </div>
            </div>
          </div>

          <div className="hero-metrics-card">
            <div className="metric-item">
              <span className="metric-val">100%</span>
              <span className="metric-lbl">Secure OTP</span>
            </div>

            <div className="metric-divider"></div>

            <div className="metric-item">
              <span className="metric-val">Fast</span>
              <span className="metric-lbl">Email Delivery</span>
            </div>

            <div className="metric-divider"></div>

            <div className="metric-item">
              <span className="metric-val">24/7</span>
              <span className="metric-lbl">Self Service</span>
            </div>
          </div>
        </div>

        <div className="hero-footer-text">
          Snap4Me Enterprise Engine • System v2.4.0
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="dashboard-login-panel">
        <div className="login-panel-inner">
          <div className="right-panel-logo">
            <img src={logo} alt="Snap4Me" />
          </div>

          <div className="form-card">
            <div className="form-header">
              <h1>
                {step === "email" && "Forgot Password?"}
                {step === "otp" && "Verify OTP"}
                {step === "reset" && "Reset Password"}
              </h1>
              <p>
                {step === "email" &&
                  "Enter your registered email address and Get your verification code"}
                {step === "otp" &&
                  "Enter the 6-digit verification code sent to your email."}
                {step === "reset" &&
                  "Create a new password for your account."}
              </p>
            </div>

            {/* ERROR BANNER */}
            {error && (
              <div className="dashboard-error-banner">
                <span className="error-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            {/* SUCCESS BANNER */}
            {success && (
              <div className="dashboard-success-banner">
                <span className="success-icon">✓</span>
                <span>{success}</span>
              </div>
            )}

            {/* =====================================================
                STEP 1: EMAIL
            ===================================================== */}
            {step === "email" && (
              <form
                onSubmit={handleSendOTP}
                className="dashboard-form"
              >
                <div className="input-field-group">
                  <label htmlFor="forgot-email">Email</label>

                  <div className="input-wrapper">
                    <span className="input-icon">&#x2709;</span>

                    <input
                      id="forgot-email"
                      type="email"
                      placeholder="operator@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="dashboard-submit-btn"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="btn-loading-state">
                      <span className="btn-spinner"></span>
                      Sending OTP...
                    </span>
                  ) : (
                    <>
                      Send OTP
                      <span className="btn-arrow">→</span>
                    </>
                  )}
                </button>
              </form>
            )}

            {/* =====================================================
                STEP 2: VERIFY OTP
            ===================================================== */}
            {step === "otp" && (
              <form
                onSubmit={handleVerifyOTP}
                className="dashboard-form"
              >
                {/* EMAIL (DISABLED) */}
                <div className="input-field-group">
                  <label htmlFor="reset-email">Email</label>

                  <div className="input-wrapper">
                    <span className="input-icon">&#x2709;</span>

                    <input
                      id="reset-email"
                      type="email"
                      value={email}
                      disabled
                    />
                  </div>
                </div>

                {/* OTP INPUT */}
                <div className="input-field-group">
                  <label htmlFor="otp">Verification Code</label>

                  <div className="input-wrapper">
                    <span className="input-icon">#</span>

                    <input
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={(e) =>
                        setOtp(
                          e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6)
                        )
                      }
                      disabled={isLoading}
                      autoComplete="one-time-code"
                      required
                    />
                  </div>
                </div>

                {/* VERIFY OTP BUTTON */}
                <button
                  type="submit"
                  className="dashboard-submit-btn"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="btn-loading-state">
                      <span className="btn-spinner"></span>
                      Verifying OTP...
                    </span>
                  ) : (
                    <>
                      Verify OTP
                      <span className="btn-arrow">→</span>
                    </>
                  )}
                </button>

                {/* RESEND OTP BUTTON */}
                <button
                  type="button"
                  className="forgot-back-btn"
                  onClick={handleResendOTP}
                  disabled={isLoading}
                  style={{
                    width: "100%",
                    marginTop: "10px",
                  }}
                >
                  Resend OTP
                </button>
              </form>
            )}

            {/* =====================================================
                STEP 3: RESET PASSWORD
            ===================================================== */}
            {step === "reset" && (
              <form
                onSubmit={handleResetPassword}
                className="dashboard-form"
              >
                {/* NEW PASSWORD */}
                <div className="input-field-group">
                  <label htmlFor="new-password">New Password</label>

                  <div className="password-input-wrapper">
                    <span className="input-icon">●</span>

                    <input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) =>
                        setNewPassword(e.target.value)
                      }
                      disabled={isLoading}
                      autoComplete="new-password"
                      required
                    />

                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() =>
                        setShowNewPassword(!showNewPassword)
                      }
                      aria-label={
                        showNewPassword
                          ? "Hide password"
                          : "Show password"
                      }
                    >
                      {showNewPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                {/* CONFIRM PASSWORD */}
                <div className="input-field-group">
                  <label htmlFor="confirm-password">
                    Confirm Password
                  </label>

                  <div className="password-input-wrapper">
                  <span className="input-icon">●</span>

                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) =>
                      setConfirmPassword(e.target.value)
                    }
                    disabled={isLoading}
                    autoComplete="new-password"
                    required
                  />

                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() =>
                      setShowConfirmPassword(!showConfirmPassword)
                    }
                    aria-label={
                      showConfirmPassword
                        ? "Hide password"
                        : "Show password"
                    }
                  >
                    {showConfirmPassword ? "Hide" : "Show"}
                  </button>
                </div>
                </div>

                {/* RESET PASSWORD BUTTON */}
                <button
                  type="submit"
                  className="dashboard-submit-btn"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="btn-loading-state">
                      <span className="btn-spinner"></span>
                      Resetting Password...
                    </span>
                  ) : (
                    <>
                      Reset Password
                      <span className="btn-arrow">→</span>
                    </>
                  )}
                </button>
              </form>
            )}

            {/* BACK TO LOGIN */}
            <div className="forgot-footer-actions">
              <button
                type="button"
                className="forgot-back-btn"
                onClick={() => navigate("/login")}
                disabled={isLoading}
              >
                ← Back to Login
              </button>
            </div>

            <div className="panel-footer">
              <span className="footer-lock">🔒</span>
              <p>Protected area. Authorized personnel only.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;